import {
  createApp,
  getCurrentInstance,
  onMounted,
  ref,
  defineAsyncComponent,
} from 'vue';
import { createI18n } from 'vue-i18n';
import { lang } from './utils/utils';
import PrimeVue from 'primevue/config';
import PhotoSwipeComponent from './components/PhotoSwipeComponent.vue';
import SwiperComponent from './components/SwiperComponent.vue';
import FullTextSearchComponent from './components/FullTextSearchComponent.vue';
import 'media-chrome';

declare const APP_VERSION: string;
declare const SENTRY_DSN: string;

const main = async () => {
  // Async load the vue module
  const Sentry = await import(/* webpackChunkName: "sentry" */ '@sentry/vue');
  const { Integrations } = await import(
    /* webpackChunkName: "tracing" */ '@sentry/tracing'
  );

  const { default: Headroom } = await import(
    /* webpackChunkName: "headroom" */ 'headroom.js'
  );

  const i18n = createI18n({
    legacy: false,
    locale: lang, // set locale
    fallbackLocale: 'en',
  });

  await import(/* webpackChunkName: "inert" */ 'wicg-inert');
  const {
    acceptAllConsentGroups,
    acceptConsentGroups,
    consentManagerVisible,
    consentSettings,
    hasConsent,
    loadScripts,
    toggleConsentManager,
  } = await import(/* webpackChunkName: "consent" */ './utils/consent');
  const { copyText } = await import(
    /* webpackChunkName: "clipboard" */ './utils/clipboard'
  );
  const { default: downloadPDF } = await import(
    /* webpackChunkName: "pdf" */ './utils/pdf'
  );
  const { downloadPopupVisible, toggleDownloadPopup, imageId } = await import(
    /* webpackChunkName: "image" */ './utils/download'
  );
  const { shareModalVisible, toggleShareModal, shareImage } = await import(
    /* webpackChunkName: "utils-share" */ './utils/share'
  );
  const { default: matchHeights } = await import(
    /* webpackChunkName: "clipboard" */ './utils/match-heights'
  );
  const { default: swiper, activeIndex } = await import(
    /* webpackChunkName: "utils-swiper" */ './utils/swiper'
  );

  const { hasStarredObjects, updateStarIcon } = await import(
    /* webpackChunkName: "utils-star" */ './utils/star'
  );
  const { default: star } = await import(
    /* webpackChunkName: "star" */ './directives/star'
  );
  const { default: lazy } = await import(
    /* webpackChunkName: "lazy" */ './directives/lazy'
  );

  const { default: scrollTarget } = await import(
    /* webpackChunkName: "scroll-target" */ './directives/scrollTarget'
  );
  const { default: suggestions } = await import(
    /* webpackChunkName: "suggestions" */ './directives/suggestions'
  );

  const { navigationVisible, toggleNavigation } = await import(
    /* webpackChunkName: "navigation" */ './utils/navigation'
  );
  const {
    notificationVisible,
    notificationIcon,
    showNotification,
    hideNotification,
  } = await import(
    /* webpackChunkName: "notification" */ './utils/notification'
  );
  const { currentSuggestion, setCurrentSuggestion } = await import(
    /* webpackChunkName: "utils/suggestions" */ './utils/suggestions'
  );

  // Create our vue instance
  const vm = createApp({
    el: '#page-container',
    components: {
      PhotoSwipeComponent: PhotoSwipeComponent,
      SwiperComponent: SwiperComponent,
      FullTextSearchComponent: FullTextSearchComponent,
      SwiperPaginationComponent: defineAsyncComponent(
        () =>
          import(
            /* webpackChunkName: "swiper-pagination" */ './components/SwiperPaginationComponent.vue'
          )
      ),
      SearchComponent: defineAsyncComponent(
        () =>
          import(
            /* webpackChunkName: "search" */ './components/SearchComponent.vue'
          )
      ),
      ObjectNavigationComponent: defineAsyncComponent(
        () =>
          import(
            /* webpackChunkName: "search" */ './components/ObjectNavigationComponent.vue'
          )
      ),
      RelatedObjectsComponent: defineAsyncComponent(
        () =>
          import(
            /* webpackChunkName: "related" */ './components/RelatedObjectsComponent.vue'
          )
      ),
      MyAlbumComponent: defineAsyncComponent(
        () =>
          import(
            /* webpackChunkName: "my-album" */ './components/MyAlbumComponent.vue'
          )
      ),
      ShareComponent: defineAsyncComponent(
        () =>
          import(
            /* webpackChunkName: "share" */ './components/ShareComponent.vue'
          )
      ),
    },

    setup(): any {
      const instance = getCurrentInstance();
      const heroMounted = ref(false);

      if (consentSettings.value) {
        loadScripts();
      }

      onMounted(async () => {
        if (Headroom.cutsTheMustard) {
          const headroomElement = document.querySelector('[data-headroom]');

          if (headroomElement) {
            const hr = new Headroom(headroomElement, { tolerance: 10 });
            hr.init();
          }
        }

        const masonries: HTMLElement[] = Array.from(
          document.querySelectorAll('[data-msnry]')
        );
        if (masonries.length) {
          const { default: initMasonry } = await import(
            /* webpackChunkName: "masonry" */ './utils/masonry'
          );
          const { shuffle } = await import(
            /* webpackChunkName: "shuffle" */ './utils/utils'
          );

          masonries.forEach((masonry) => {
            if (masonry.dataset.shuffle !== undefined) {
              shuffle(masonry);
            }
            initMasonry(masonry).then(() => {
              if (masonry.dataset.shuffle !== undefined) {
                /* Use IntersectionObserver to remove all hero images that are
                 * not visible, so that they are not loaded at all.
                 */
                const revealImage = (element: Element) => {
                  const sourceTags = Array.from(
                    element.querySelectorAll('source')
                  );
                  const img = element.querySelector('img');

                  sourceTags.forEach((sourceTag) => {
                    sourceTag.srcset = sourceTag.dataset.srcset || '';

                    delete sourceTag.dataset.srcset;
                  });
                  if (img) {
                    img.srcset = img.dataset.srcset || '';
                    img.src = img.dataset.src || '';
                    delete img.dataset.srcset;
                    delete img.dataset.src;
                    element.classList.add('translate-y');
                    element.classList.remove('opacity-0');
                  }
                };

                const tiles = Array.from(
                  masonry.querySelectorAll('[data-tile]')
                );

                if ('IntersectionObserver' in window) {
                  const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                      if (!entry.isIntersecting) {
                        entry.target.parentNode?.removeChild(entry.target);
                      } else {
                        revealImage(entry.target);
                      }
                      observer.unobserve(entry.target);
                    });
                  });
                  tiles.forEach((tile) => {
                    observer.observe(tile);
                  });
                } else {
                  tiles.forEach((tile) => {
                    revealImage(tile);
                  });
                }
              }
            });
          });
        }

        updateStarIcon();
      });

      instance?.proxy?.$nextTick(() => {
        matchHeights('[data-same-height]');
      });

      document.addEventListener('contextmenu', (event): void => {
        const { target } = event;

        if ((target as HTMLElement).tagName === 'IMG') {
          event.preventDefault();
        }
      });

      const historyBack = () => {
        window.history.go(-1);
      };

      const removePlaceholder = (event: Event) => {
        const placeholder = document.querySelector('[data-placeholder]');
        if (placeholder) {
          const { nextElementSibling } = placeholder;

          if (placeholder && placeholder.parentElement) {
            placeholder.parentElement.removeChild(placeholder);
            nextElementSibling?.classList.remove('absolute');
            if (nextElementSibling) {
              Array.from(
                nextElementSibling.querySelectorAll('figcaption')
              ).forEach((caption) => {
                caption.classList.remove('opacity-0');
              });
            }
          }
        }
      };

      return {
        navigationVisible,
        toggleNavigation,
        notificationVisible,
        notificationIcon,
        showNotification,
        hideNotification,
        copyText,
        heroMounted,
        hasStarredObjects,
        downloadPDF,
        downloadPopupVisible,
        toggleDownloadPopup,
        imageId,
        shareModalVisible,
        toggleShareModal,
        shareImage,
        swiper,
        activeIndex,
        historyBack,
        removePlaceholder,
        currentSuggestion,
        setCurrentSuggestion,
        consentSettings,
        consentManagerVisible,
        toggleConsentManager,
        acceptConsentGroups,
        acceptAllConsentGroups,
        hasConsent,
      };
    },
  });

  vm.directive('star', star);
  vm.directive('lazy', lazy);
  vm.directive('scroll-target', scrollTarget);
  vm.directive('suggestions', suggestions);
  vm.use(i18n);
  vm.use(PrimeVue);

  Sentry.init({
    app: vm,
    dsn: SENTRY_DSN,
    release: APP_VERSION,
    logErrors: process.env.NODE_ENV !== 'production',
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.2,
  });

  vm.mount('#page-container');
  return vm;
};

// Execute async function
main().then((vm) => {
  setTimeout(() => {
    document.documentElement.style.setProperty(
      '--logo-animation-duration',
      '200ms'
    );
  }, 250);
});

// Accept HMR as per: https://webpack.js.org/api/hot-module-replacement#accept
if (module.hot) {
  module.hot.accept();
}
